import { Alert, Button, Card, Flex, Form, Result } from 'antd'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { client as mainApiClient } from 'src/api/main/client.gen'
import { useShowApiErrors } from 'src/utils'

import { THIRD_PARTY_CUSTOM_APP_TYPE } from '../../constants/constants'

import { CLIENT_TYPE_POLICY } from './constants'
import { useOidcClient } from './hooks'
import { OidcClientSecretAlert } from './OidcClientSecretAlert'
import { OidcConfigurationCard } from './OidcConfigurationCard'
import { OidcEnableCard } from './OidcEnableCard'
import { OidcQuickStartCard } from './OidcQuickStartCard'
import { OidcReferenceCard } from './OidcReferenceCard'
import type { OidcConfigFormValues } from './types'
import { listFromMultiline, multilineFromList } from './utils'

type Props = {
  applicationId: number
  applicationName: string
  applicationToken?: string
  applicationType: string
  organizationId: number
}

// Default client type when the admin clicks "Enable OIDC". Confidential
// is the only safe default: it can be created with no redirect_uris because
// it defaults to the client-credentials grant. Switching to Public happens
// later in the configuration form, where redirect URIs can be entered and
// validated before the switch is sent to the backend.
const DEFAULT_NEW_CLIENT_TYPE = 'confidential' as const

const isValidApplicationId = (id: number): boolean =>
  Number.isFinite(id) && id > 0

export const AppOidcPanel = ({
  applicationId,
  applicationName,
  applicationToken,
  applicationType,
  organizationId
}: Props) => {
  const { t } = useTranslation()
  const showApiErrors = useShowApiErrors()
  const [visibleSecret, setVisibleSecret] = useState<string | null>(null)
  const [configForm] = Form.useForm<OidcConfigFormValues>()

  const isThirdParty = applicationType === THIRD_PARTY_CUSTOM_APP_TYPE
  const idValid = isValidApplicationId(applicationId)

  const baseURL = String(mainApiClient.getConfig().baseURL ?? '').replace(
    /\/$/,
    ''
  )

  const {
    oidcClient,
    isLoading,
    isError,
    refetch,
    createClient,
    isCreating,
    updateClient,
    isUpdating,
    rotateClientSecret,
    isRotating,
    revokeClient,
    isRevoking
  } = useOidcClient({
    applicationId,
    enabled: isThirdParty && idValid,
    onSecretRevealed: setVisibleSecret,
    onSecretCleared: () => setVisibleSecret(null)
  })

  if (!isThirdParty) {
    return (
      <Alert
        type="info"
        showIcon
        title={t(
          'workspace.apps.oidc.nonThirdPartyHint',
          'OIDC settings are available only for third-party custom applications.'
        )}
      />
    )
  }

  if (!idValid) {
    return (
      <Alert
        type="error"
        showIcon
        title={t(
          'oidc.invalid_application',
          'Cannot load OIDC settings — the application identifier is missing or invalid.'
        )}
      />
    )
  }

  const isOidcActive = !!oidcClient && !oidcClient.is_revoked

  const syncFormToClient = (next: typeof oidcClient) => {
    if (!next) return
    configForm.setFieldsValue({
      algorithm: (next.algorithm as '' | 'RS256' | 'HS256') ?? '',
      allow_client_credentials: next.allow_client_credentials ?? true,
      allowed_origins: multilineFromList(next.allowed_origins),
      client_type:
        (next.client_type as OidcConfigFormValues['client_type'] | undefined) ??
        'confidential',
      grant_type:
        (next.grant_type as OidcConfigFormValues['grant_type']) ??
        'client-credentials',
      name: next.name ?? applicationName,
      pkce_required: next.pkce_required ?? true,
      post_logout_redirect_uris: multilineFromList(
        next.post_logout_redirect_uris
      ),
      redirect_uris: multilineFromList(next.redirect_uris)
    })
  }

  const handleEnable = () => {
    if (!idValid) return
    const policy = CLIENT_TYPE_POLICY[DEFAULT_NEW_CLIENT_TYPE]

    createClient(
      {
        body: {
          application: applicationId,
          organization: organizationId,
          name: applicationName,
          client_type: DEFAULT_NEW_CLIENT_TYPE,
          grant_type: policy.defaultGrant,
          allow_client_credentials: policy.defaultAllowClientCredentials,
          pkce_required: policy.defaultPkceRequired
        } as never
      },
      { onError: (error) => showApiErrors(error) }
    )
  }

  const handleReenable = () => {
    if (!oidcClient) return
    updateClient(
      {
        body: { is_revoked: false },
        path: { id: oidcClient.id }
      },
      {
        onSuccess: () => {
          // Rotate secret only for confidential clients — public clients
          // never receive a secret.
          if (oidcClient.client_type !== 'public') {
            rotateClientSecret({
              body: {} as never,
              path: { id: oidcClient.id }
            })
          }
        },
        onError: (error) => showApiErrors(error)
      }
    )
  }

  const handleUpdate = (values: OidcConfigFormValues) => {
    if (!oidcClient) return
    updateClient(
      {
        body: {
          algorithm: values.algorithm || undefined,
          allow_client_credentials: values.allow_client_credentials,
          allowed_origins: listFromMultiline(values.allowed_origins),
          client_type: values.client_type,
          grant_type: values.grant_type,
          name: values.name,
          pkce_required: values.pkce_required,
          post_logout_redirect_uris: listFromMultiline(
            values.post_logout_redirect_uris
          ),
          redirect_uris: listFromMultiline(values.redirect_uris)
        } as never,
        path: { id: oidcClient.id }
      },
      {
        // Re-seed the form from the canonical server response so the user's
        // typed values are reconciled with anything the backend normalized
        // (e.g. trimmed URIs). This keeps the "Reset" button accurate.
        onSuccess: (client) => syncFormToClient(client as typeof oidcClient),
        onError: (error) => showApiErrors(error, configForm)
      }
    )
  }

  const handleRotateSecret = () => {
    if (!oidcClient) return
    rotateClientSecret(
      { body: {} as never, path: { id: oidcClient.id } },
      { onError: (error) => showApiErrors(error) }
    )
  }

  const handleRevoke = () => {
    if (!oidcClient) return
    revokeClient(
      { body: {} as never, path: { id: oidcClient.id } },
      { onError: (error) => showApiErrors(error) }
    )
  }

  if (isLoading) {
    return <Card variant="outlined" size="small" loading />
  }

  if (isError) {
    return (
      <Card variant="outlined" size="small">
        <Result
          status="warning"
          title={t(
            'oidc.load_failed_title',
            'Could not load OIDC configuration'
          )}
          subTitle={t(
            'oidc.load_failed_description',
            'Check your connection and try again. The OIDC client (if one exists) is unchanged.'
          )}
          extra={
            <Button size="small" type="primary" onClick={() => void refetch()}>
              {t('retry', 'Retry')}
            </Button>
          }
        />
      </Card>
    )
  }

  return (
    <Flex vertical gap={14}>
      {visibleSecret ? (
        <OidcClientSecretAlert
          secret={visibleSecret}
          onClose={() => setVisibleSecret(null)}
        />
      ) : null}

      {!oidcClient ? (
        <OidcEnableCard isPending={isCreating} onEnable={handleEnable} />
      ) : !isOidcActive ? (
        <OidcDisabledCard
          isPublic={oidcClient.client_type === 'public'}
          isPending={isUpdating || isRotating}
          onReenable={handleReenable}
        />
      ) : (
        <OidcConfigurationCard
          applicationName={applicationName}
          client={oidcClient}
          form={configForm}
          isSaving={isUpdating}
          isRotating={isRotating}
          isRevoking={isRevoking}
          onSubmit={handleUpdate}
          onRotateSecret={handleRotateSecret}
          onRevoke={handleRevoke}
        />
      )}

      {isOidcActive && oidcClient?.client_id ? (
        applicationToken ? (
          <OidcQuickStartCard
            apiUrl={baseURL}
            applicationId={applicationId}
            appName={applicationName}
            appToken={applicationToken}
            clientId={oidcClient.client_id}
          />
        ) : (
          <Alert
            type="warning"
            showIcon
            title={t(
              'oidc.missing_app_token_title',
              'App token is not assigned yet'
            )}
            description={t(
              'oidc.missing_app_token_description',
              'The scaffold command becomes available once this application has a saas-app-token. Refresh after the token is provisioned.'
            )}
          />
        )
      ) : null}

      {isOidcActive ? <OidcReferenceCard baseURL={baseURL} /> : null}
    </Flex>
  )
}

const OidcDisabledCard = ({
  isPublic,
  isPending,
  onReenable
}: {
  isPublic: boolean
  isPending: boolean
  onReenable: () => void
}) => {
  const { t } = useTranslation()

  return (
    <Card variant="outlined" size="small">
      <Alert
        type="warning"
        showIcon
        title={t('oidc.disabled_title', 'OIDC is currently disabled')}
        description={
          isPublic
            ? t(
                'oidc.disabled_description_public',
                'The previous configuration is preserved. Re-enabling restores the Public client without minting a new secret (Public clients never have one).'
              )
            : t(
                'oidc.disabled_description_confidential',
                'The previous configuration is preserved. Re-enabling will mint a fresh client secret (shown once).'
              )
        }
        action={
          <Button
            size="small"
            type="primary"
            loading={isPending}
            onClick={onReenable}
          >
            {t('oidc.reenable', 'Re-enable OIDC')}
          </Button>
        }
      />
    </Card>
  )
}
